"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale } from "./LocaleProvider";

const copy = {
  en: {
    walton: "WhatsApp Walton-on-Thames",
    stanmore: "WhatsApp Stanmore",
    label: "Message us on WhatsApp",
    title: "Message us",
  },
  ro: {
    walton: "WhatsApp Walton-on-Thames",
    stanmore: "WhatsApp Stanmore",
    label: "Scrieți-ne pe WhatsApp",
    title: "Scrieți-ne",
  },
} as const;

type Props = {
  suspend?: boolean;
};

export default function WhatsAppButton({ suspend }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const t = copy[useLocale()];
  const hidden = !!suspend;

  // ✅ EgoDent WhatsApp numbers (NO "+" in wa.me)
  const WALTON_NUMBER = "447546703167";
  const STANMORE_NUMBER = "447523836323";

  useEffect(() => {
    if (suspend) setOpen(false);
  }, [suspend]);

  useEffect(() => {
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!open) return;
      if (!rootRef.current) return;
      const t = e.target as Node | null;
      if (t && !rootRef.current.contains(t)) setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={[
        "fixed right-5 bottom-5 z-[9999]",
        hidden
          ? "opacity-0 pointer-events-none translate-y-2"
          : "opacity-100 pointer-events-auto translate-y-0",
        "transition-all duration-300 ease-out",
      ].join(" ")}
    >
      {/* Panel */}
      <div
        className={[
          "absolute right-0 bottom-[68px]",
          open
            ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
            : "opacity-0 scale-95 translate-y-2 pointer-events-none",
          "transition-all duration-200 ease-out origin-bottom-right",
        ].join(" ")}
      >
        <div className="min-w-[240px] rounded-2xl border border-white/10 bg-black/75 backdrop-blur-xl shadow-2xl p-2">
          <a
            href={`https://wa.me/${WALTON_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm text-white hover:bg-white/5 transition"
            onClick={() => setOpen(false)}
          >
            <span className="font-medium">{t.walton}</span>
          </a>

          <a
            href={`https://wa.me/${STANMORE_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm text-white hover:bg-white/5 transition"
            onClick={() => setOpen(false)}
          >
            <span className="font-medium">{t.stanmore}</span>
          </a>
        </div>
      </div>

      {/* Main button (round like Call) */}
      <button
        type="button"
        aria-label={t.label}
        title={t.title}
        onClick={() => setOpen((v) => !v)}
        className={[
          "h-14 w-14 rounded-full bg-emerald-400 text-black",
          "shadow-[0_10px_40px_rgba(0,0,0,0.55)] border border-white/10",
          "grid place-items-center",
          "transition-transform duration-150",
          "hover:scale-110 active:scale-[0.96]",
        ].join(" ")}
      >
        {/* message/chat icon (brand-neutral) */}
        <svg viewBox="0 0 24 24" className="h-7 w-7" aria-hidden="true">
          <path
            fill="currentColor"
            d="M20 2H4a2 2 0 0 0-2 2v16l4-3h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Zm0 13H5.3L4 16.1V4h16v11Z"
          />
        </svg>
      </button>
    </div>
  );
}
